/**
 * Attendance type definitions matching backend schemas exactly.
 * These types ensure type safety and consistency with the API.
 */
import { z } from "zod"

// Base attendance properties matching AttendanceBase schema
export interface AttendanceBase {
  enrollment_id: string
  class_session_id: string
  student_id: string
  class_date: string
  class_time: string | null
  status:
    | "present"
    | "absent"
    | "excused"
    | "late"
    | "makeup"
    | "cancelled"
    | null
  check_in_time: string | null
  check_out_time: string | null
  is_makeup_class: boolean | null
  makeup_for_date: string | null
  instructor_notes: string | null
  skills_practiced: Record<string, unknown> | null
  behavior_notes: string | null
  parent_notification_sent: boolean | null
  marked_by_user_id: string | null
  notes: string | null
}

// Create attendance data matching AttendanceCreate schema
export interface AttendanceCreate extends AttendanceBase {}

// Update attendance data matching AttendanceUpdate schema
export interface AttendanceUpdate {
  enrollment_id?: string
  class_session_id?: string
  student_id?: string
  class_date?: string
  class_time?: string | null
  status?:
    | "present"
    | "absent"
    | "excused"
    | "late"
    | "makeup"
    | "cancelled"
    | null
  check_in_time?: string | null
  check_out_time?: string | null
  is_makeup_class?: boolean | null
  makeup_for_date?: string | null
  instructor_notes?: string | null
  skills_practiced?: Record<string, unknown> | null
  behavior_notes?: string | null
  parent_notification_sent?: boolean | null
  marked_by_user_id?: string | null
  notes?: string | null
}

// Full attendance response matching AttendanceResponse schema
export interface AttendancePublic extends AttendanceBase {
  id: string
  created_by: string
  created_at: string
  updated_by: string
  updated_at: string
}

// Response wrapper for API calls
export interface AttendanceResponse extends AttendancePublic {
  // Enriched fields populated by the backend
  student_name?: string | null
  class_name?: string | null
  session_name?: string | null
}

// Paginated list response matching AttendanceListResponse schema
export interface AttendanceListResponse {
  items: AttendanceResponse[]
  total: number
  page: number
  per_page: number
  pages: number
  meta: AttendanceListMeta
}

export interface AttendanceListMeta {
  sort_by: "created_at" | "updated_at"
  sort_order: "asc" | "desc"
  filters: AttendanceFilters
}

// Form data interfaces for UI components
export interface AttendanceFormData {
  enrollment_id?: string
  class_session_id?: string
  student_id?: string
  class_date?: string
  class_time?: string | null
  status?:
    | "present"
    | "absent"
    | "excused"
    | "late"
    | "makeup"
    | "cancelled"
    | null
  check_in_time?: string | null
  check_out_time?: string | null
  is_makeup_class?: boolean | null
  makeup_for_date?: string | null
  instructor_notes?: string | null
  skills_practiced?: Record<string, unknown> | null
  behavior_notes?: string | null
  parent_notification_sent?: boolean | null
  marked_by_user_id?: string | null
  notes?: string | null
}

export interface AttendanceUpdateFormData {
  enrollment_id?: string
  class_session_id?: string
  student_id?: string
  class_date?: string
  class_time?: string | null
  status?:
    | "present"
    | "absent"
    | "excused"
    | "late"
    | "makeup"
    | "cancelled"
    | null
  check_in_time?: string | null
  check_out_time?: string | null
  is_makeup_class?: boolean | null
  makeup_for_date?: string | null
  instructor_notes?: string | null
  skills_practiced?: Record<string, unknown> | null
  behavior_notes?: string | null
  parent_notification_sent?: boolean | null
  marked_by_user_id?: string | null
  notes?: string | null
}

// Filter interface for API queries
export interface AttendanceFilters {
  limit?: number
  skip?: number
  search?: string
  sort_by?: "created_at" | "updated_at"
  sort_order?: "asc" | "desc"
  created_by?: string
  created_after?: string
  created_before?: string
  updated_after?: string
  updated_before?: string
  student_id?: string
  status?:
    | "present"
    | "absent"
    | "excused"
    | "late"
    | "makeup"
    | "cancelled"
}

// Component prop interfaces following sophisticated pattern
export interface AddAttendanceProps {
  onSuccess?: () => void
  onCancel?: () => void
}

export interface EditAttendanceProps {
  attendance: AttendanceResponse
  onSuccess?: () => void
  onCancel?: () => void
}

export interface DeleteAttendanceProps {
  attendance: AttendanceResponse
  onSuccess?: () => void
  onCancel?: () => void
}

// Zod validation schemas for forms
export const attendanceCreateSchema = z.object({
  enrollment_id: z.string().trim().max(32),
  class_session_id: z.string().trim().max(32),
  student_id: z.string().trim().max(32),
  class_date: z.string(),
  class_time: z.string().trim().nullable().optional(),
  status: z
    .enum(["present", "absent", "excused", "late", "makeup", "cancelled"])
    .nullable()
    .optional(),
  check_in_time: z.string().nullable().optional(),
  check_out_time: z.string().nullable().optional(),
  is_makeup_class: z.boolean().nullable().optional(),
  makeup_for_date: z.string().nullable().optional(),
  instructor_notes: z.string().trim().nullable().optional(),
  skills_practiced: z.any().nullable().optional(),
  behavior_notes: z.string().trim().nullable().optional(),
  parent_notification_sent: z.boolean().nullable().optional(),
  marked_by_user_id: z.string().trim().max(32).nullable().optional(),
  notes: z.string().trim().nullable().optional(),
})

export const attendanceUpdateSchema = z.object({
  enrollment_id: z.string().trim().max(32).optional(),
  class_session_id: z.string().trim().max(32).optional(),
  student_id: z.string().trim().max(32).optional(),
  class_date: z.string().optional(),
  class_time: z.string().trim().nullable().optional(),
  status: z
    .enum(["present", "absent", "excused", "late", "makeup", "cancelled"])
    .nullable()
    .optional(),
  check_in_time: z.string().nullable().optional(),
  check_out_time: z.string().nullable().optional(),
  is_makeup_class: z.boolean().nullable().optional(),
  makeup_for_date: z.string().nullable().optional(),
  instructor_notes: z.string().trim().nullable().optional(),
  skills_practiced: z.any().nullable().optional(),
  behavior_notes: z.string().trim().nullable().optional(),
  parent_notification_sent: z.boolean().nullable().optional(),
  marked_by_user_id: z.string().trim().max(32).nullable().optional(),
  notes: z.string().trim().nullable().optional(),
})

// Type inference from Zod schemas
export type AttendanceCreateInput = z.infer<typeof attendanceCreateSchema>
export type AttendanceUpdateInput = z.infer<typeof attendanceUpdateSchema>
